<template>
  <template v-if="isEmpty(value)">
    {{ fallbackValue }}
  </template>

  <SoTooltip
    v-else-if="type === ColumnType.CHIP && typeof value === 'object'"
    :text="value.tooltip"
    :disabled="!value.tooltip"
  >
    <SoChip v-bind="value.props" />
  </SoTooltip>

  <div v-else-if="type === ColumnType.CHIPS">
    <SoTooltip :text="value.tooltip" :disabled="!value.tooltip">
      <span v-if="value.value">
        <span
          :class="{
            'text-success font-weight-medium': value.highlight,
          }"
        >
          {{ value.value }}
        </span>
      </span>
      <SoChip
        v-for="(chip, index) in value.props"
        v-bind="chip"
        :key="`chip-${index}`"
        class="ma-1"
      />
    </SoTooltip>
  </div>

  <SoStatus v-else-if="type === ColumnType.STATUS" v-bind="value.props" />

  <div v-else-if="type === ColumnType.LIST" data-testid="so-data-table-list">
    <v-list>
      <v-list-item v-for="(val, name) in value" :key="name">
        {{ name }}: {{ val }}
      </v-list-item>
    </v-list>
  </div>

  <div
    v-else-if="type === ColumnType.STATUSES"
    class="d-flex flex-wrap gap-1 py-2"
  >
    <SoStatus
      v-for="(status, index) in value"
      v-bind="status.props"
      :key="`status-${index}`"
    />
  </div>

  <template v-else-if="type === ColumnType.DATE">
    <span class="text-no-wrap">{{ value ? formatDate(value) : "" }}</span>
  </template>

  <template v-else-if="type === ColumnType.DATE_TIME">
    <span class="text-no-wrap">{{ value ? formatDateTime(value) : "" }}</span>
  </template>

  <template v-else-if="type === ColumnType.DATE_PERIOD">
    <span class="text-no-wrap">
      {{
        value?.datePeriod?.from && value?.datePeriod?.to
          ? formatDatePeriod(value.datePeriod.from, value.datePeriod.to)
          : ""
      }}
    </span>
  </template>

  <template v-else-if="type === ColumnType.MONEY">
    <span class="text-no-wrap">
      {{ value || value === 0 ? formatMoney({ amount: value }) : "" }}
    </span>
  </template>

  <template v-else-if="type === ColumnType.MONEY_COLORED">
    <span
      :class="{
        'text-critical': value > 0,
        'text-normal': value < 0,
      }"
      class="text-no-wrap"
    >
      {{ value || value === 0 ? formatMoney({ amount: -value }) : "" }}
    </span>
  </template>

  <template v-else-if="type === ColumnType.MONEY_ARROW">
    <span v-if="value || value === 0" class="text-no-wrap">
      {{ formatMoney({ amount: Math.abs(value) }) }}
      <span v-if="value > 0" class="ml-2 text-success"> ↑ </span>
      <span v-if="value < 0" class="ml-2 text-danger"> ↓ </span>
    </span>
  </template>

  <template v-else-if="type === ColumnType.YES_OR_NO">
    <span class="text-no-wrap">{{ yesNo(value) }}</span>
  </template>

  <template v-else-if="type === ColumnType.ICON">
    <p class="d-flex align-center">
      <span>{{ value.text }}</span>
      <SoIcon v-if="value.props.icon" v-bind="value.props" />
    </p>
  </template>

  <template v-else-if="type === ColumnType.COPY_TO_CLIPBOARD">
    <div class="d-flex align-center" data-testid="so-data-table-copy-item">
      {{ value }}
      <SoCopyToClipboard :plain-text="value">
        <template #button="{ copyToClipboard }">
          <v-btn
            type="sidebar-icon"
            size="x-small"
            variant="plain"
            icon="copy sm"
            @click.stop="copyToClipboard"
          />
        </template>
      </SoCopyToClipboard>
    </div>
  </template>

  <template v-else-if="type === ColumnType.LINK">
    <div
      class="d-flex align-center"
      data-testid="so-data-table-customer-link-item"
    >
      <SoLink
        :text="value.text"
        icon="external-link sm"
        @click="$router.push({ path: value.path })"
      />
    </div>
  </template>

  <template v-else-if="type === ColumnType.EXTERNAL_LINK">
    <div class="d-flex align-center">
      <SoLink
        v-if="value.props.url"
        :text="value.props.text"
        :href="value.props.url"
        icon="external-link sm"
      />
      <template v-else>
        {{ value.props.text }}
      </template>
    </div>
  </template>

  <template v-else-if="type === ColumnType.PERCENTAGE">{{ value }}%</template>

  <template v-else>
    {{ value }}
  </template>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import dayjs from "dayjs"
  import SoChip from "@/components/SoChip.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import { formatMoney } from "@/helpers/moneyHelpers"
  import {
    formatDate,
    formatDateTime,
    formatDatePeriod,
  } from "@/helpers/dateHelper"
  import { yesNo } from "@/helpers/stringHelpers"
  import { SoDataTableColumnType as ColumnType } from "@/types/components/SoDataTable"
  import SoCopyToClipboard from "@/components/SoCopyToClipboard.vue"
  import SoLink from "@/components/SoLink.vue"
  import { isEmpty } from "@/helpers/objectHelpers"
  import SoTooltip from "@/components/SoTooltip.vue"

  export default defineComponent({
    name: "SoDataTable",
    components: {
      SoTooltip,
      SoChip,
      SoIcon,
      SoStatus,
      SoCopyToClipboard,
      SoLink,
    },
    inheritAttrs: false,
    props: {
      value: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        type: [Object, String, Number, Boolean] as PropType<any>,
        default: undefined,
      },
      type: {
        type: String as PropType<ColumnType>,
        default: undefined,
      },
      fallbackValue: {
        type: String,
        default: "",
      },
    },
    data() {
      return {
        ColumnType,
      }
    },
    methods: {
      isEmpty,
      formatDate,
      formatDateTime,
      formatDatePeriod,
      formatMoney,
      yesNo,
      dayjs,
    },
  })
</script>
